import { forwardRef } from 'react';
import { CampaignBadgeType } from './CampaignCard';
import clsx from 'clsx';
import { CampaignCardBadge } from './Badge';
import { Theme } from '@fagmobler/sanity';

export type CampaignCardV2Props = {
  headerText?: string;
  bigText?: string;
  title?: string;
  buttonText?: string;
  footerText?: string;
  className?: string;
  badgeType?: CampaignBadgeType;
  badgeText?: string;
  image?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  theme?: Theme;
};

export const CampaignCardV2 = forwardRef(function CampaignCard(
  {
    className = '',
    badgeType,
    badgeText,
    image,
    headerText,
    bigText,
    title,
    buttonText,
    footerText,
    size,
    theme = 'neutral',
    fullWidth = false,
    ...rest
  }: CampaignCardV2Props,
  ref
) {
  const badgeSale = badgeType === 'sale';
  const blackweek =
    badgeType === 'blackweek' ||
    badgeType === 'blackweek-logo-light' ||
    badgeType === 'blackweek-logo-dark';
  const blackweekLogoLight = badgeType === 'blackweek-logo-light';
  const blackweekLogoDark = badgeType === 'blackweek-logo-dark';
  const badgeNews = badgeType === 'news';
  const badgePromotion = badgeType === 'promotion';

  return (
    <div className="w-full h-full relative group underline-offset-4 @container">
      {image}
      <div
        className={clsx(
          'absolute top-0 left-0 w-full h-full bg-neutral-80 bg-opacity-30 transition-all flex flex-col items-center justify-center text-center text-neutral-0'
          // {
          //   'hover:bg-opacity-70': !fullWidth,
          // }
        )}
      >
        {badgeType && (
          <CampaignCardBadge
            badgeNews={badgeNews}
            badgePromotion={badgePromotion}
            badgeSale={badgeSale}
            blackweek={blackweek}
            blackweekLogoLight={blackweekLogoLight}
            blackweekLogoDark={blackweekLogoDark}
            badgeText={badgeText}
            large={true}
            withImage={true}
            parentType="v2"
          />
        )}
        {/* Debug size */}
        {/*<div className="absolute top-0 right-0 bg-tetiery-50">Size: {size}</div>*/}
        <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center text-center text-neutral-0">
          {headerText && theme !== 'blackWeekLogo' && theme !== 'salgLogo' && (
            <div className="hd-md @xs:hd-lg">{headerText}</div>
          )}
          {theme === 'blackWeekLogo' && (
            <div
              className="h-1/2 lg:h-[65%] w-auto aspect-square"
              style={{
                backgroundImage: 'url(/gfx/blackweek-logo-light.svg)',
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                backgroundColor: 'transparent',
              }}
            ></div>
          )}
          {theme === 'salgLogo' && (
            <div
              className="h-1/2 lg:h-[65%] w-auto aspect-square"
              style={{
                backgroundImage: 'url(/gfx/salgsalgsalg.svg)',
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                backgroundColor: 'transparent',
              }}
            ></div>
          )}
          {bigText && theme !== 'blackWeekLogo' && theme !== 'salgLogo' && (
            <div
              className={clsx(
                'group-hover:underline',

                {
                  'text-neutral-0': theme === 'neutral',
                  'text-tetiery-40': theme === 'lightYellow',
                  'text-pink-60': theme === 'garden',
                  'text-tetiery-60':
                    theme === 'blackWeekYellow' || theme === 'blackWeekBlack',
                  'hd-xl @md:hd-5xl @lg:hd-8xl': size === 'md' || size === 'lg',
                  'hd-xl @md:hd-2xl @sm:hd-5xl': size === 'sm',
                },
                '!font-bold'
              )}
            >
              {bigText}
            </div>
          )}
          {title && (
            <div
              className={clsx('max-w-[10em]', {
                'hd-md @md:hd-xl @lg:hd-7xl': size === 'lg',
                'group-hover:underline': !bigText,
                'hd-md @md:hd-3xl': size === 'md',
                'hd-md @md:hd-lg': size === 'sm',
              })}
            >
              {title}
            </div>
          )}
          {buttonText && (
            <div
              className={clsx('bg-neutral-0 text-neutral-80 px-3 py-2 mt-4', {
                hidden: size !== 'lg',
              })}
            >
              {buttonText}
            </div>
          )}
        </div>
        {footerText && theme !== 'blackWeekLogo' && (
          <div
            className={clsx('mt-auto', {
              'hd-md lg:hd-lg pb-4 md:pb-4 lg:pb-4 xl:pb-8': size === 'lg',
              'pb-4 md:pb-4 lg:pb-4 md:hd-md lg:hd-lg': size === 'md',
              'pb-2 sm:pb-4 md:pb-4 lg:pb-4': size === 'sm',
            })}
          >
            {footerText}
          </div>
        )}
      </div>
    </div>
  );
});

export default CampaignCardV2;
